import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { reactQueryPatch } from "@/react/helpers/backendRequestHelpers";
import { transformToFormData } from "@/react/helpers/transformToFormData";
import {
  landingPageSettingsPath,
  resetCommunityWelcomeBannerPreferencePath,
} from "@/react/helpers/urlHelpers";
import { useHomePageApi } from "@/react/hooks/home_page/useHomePageApi";
import { useToast } from "@circle-react-uikit/ToastV2";

const options: RequestInit = {
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  credentials: "same-origin",
};

interface MetaData {
  homePageOption: any;
  postSortOptions: any[];
  trendingPostWindowLimit: number;
}

interface Settings {
  community: any;
  community_setting: any;
}

export const useLandingPageSettingsApi = () => {
  const [settings, setSettings] = useState<Settings>({
    community: {},
    community_setting: {},
  });
  const [isDataLoaded, setDataLoaded] = useState(false);
  const [metaData, setMetaData] = useState<MetaData>({
    postSortOptions: [],
    trendingPostWindowLimit: 30,
    homePageOption: {},
  });
  const [loggedInOptions, setLoggedInOptions] = useState<any[]>([]);
  const [loggedOutOptions, setLoggedOutOptions] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { error, success } = useToast();
  const { fetchBannerData } = useHomePageApi();

  const fetchLandingPageSettings = async () => {
    setDataLoaded(false);
    try {
      const response = await fetch(landingPageSettingsPath(), options);
      if (!response.ok) {
        throw new Error("Failed to fetch landing page settings");
      }
      const data: any = await response.json();

      setSettings(data.settings);
      setMetaData({
        postSortOptions: data.post_sort_options,
        trendingPostWindowLimit: data.trending_post_window_limit,
        homePageOption: data.home_page_option,
      });
      setLoggedInOptions(data.logged_in_user_space_options);
      setLoggedOutOptions(data.logged_out_user_space_options);
    } catch (error) {
      console.error(error);
    } finally {
      setDataLoaded(true);
    }
  };

  const reSetWelcomeBannerPreference = () =>
    reactQueryPatch(resetCommunityWelcomeBannerPreferencePath());

  const updateLandingPageSettings = async (settings: object) => {
    try {
      setIsSaving(true);
      const transform = transformToFormData({
        landing_page_settings: settings,
      });

      //Cannot use react query due to implementation on open graph image.
      const response = await fetch(landingPageSettingsPath(), {
        method: "POST",
        body: transform,
        mode: "no-cors",
        ...options,
      });

      await response.json();
      success(t("changes_saved"));
      await fetchBannerData();
    } catch (err) {
      error(t("something_went_wrong"));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    dataLoaded: isDataLoaded,
    settings,
    metaData,
    loggedInUserSpaceOptions: loggedInOptions,
    loggedOutUserSpaceOptions: loggedOutOptions,
    setSettings,
    fetchLandingPageSettings,
    setLoggedInUserSpaceOptions: setLoggedInOptions,
    setLoggedOutUserSpaceOptions: setLoggedOutOptions,
    reSetWelcomeBannerPreference,
    updateLandingPageSettings,
    isSaving,
  };
};
